import Form from "common/Form/Form";
import Input from "common/Inputs/Input";
import { SizeType } from "antd/es/config-provider/SizeContext";
import Text from "common/Typography/Text";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import { Check, X } from "lucide-react";
import styled, { useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import { motion } from "motion/react";
import usePasswordSection, {
  ValidationItemData,
} from "@shared/features/onboarding/hooks/usePasswordSection";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface PasswordSectionProps {
  size?: SizeType;
  showConfirm?: boolean;
  showPlaceholder?: boolean;
  passwordLabel?: string;
  /** Overrides the "Confirm {password label}" label. */
  confirmLabel?: string;
  passwordInputTestId?: string;
  /** "new-password" lets password managers offer a strong password. */
  autoComplete?: string;
}

export const PasswordSection = ({
  size,
  showConfirm,
  showPlaceholder = true,
  passwordLabel,
  confirmLabel,
  passwordInputTestId,
  autoComplete,
}: PasswordSectionProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const { isNewLook } = useTheme();
  const {
    onPasswordChange,
    onFocus,
    validationTitle,
    validationData,
    onBlur,
    showPasswordValidation,
  } = usePasswordSection();

  const checkPasswordCondition = (validationItem: ValidationItemData) => {
    const style = { color: validationItem.color };
    if (isNewLook) {
      const Icon = validationItem.isValid ? Check : X;
      return <Icon size={14} strokeWidth={2} style={style} aria-hidden />;
    }
    if (validationItem.isValid) {
      return <CheckOutlined style={style} />;
    }
    return <CloseOutlined style={style} />;
  };

  const getPasswordErrorMessage = () => {
    if (isNewLook) {
      return (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Rules>
            <RulesTitle>{validationTitle}</RulesTitle>
            {validationData.map((itemData, index) => (
              <Rule key={index} $valid={itemData.isValid}>
                {checkPasswordCondition(itemData)}
                <span>{itemData.text}</span>
              </Rule>
            ))}
          </Rules>
        </motion.div>
      );
    }
    return (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <Text type="secondary" style={{ margin: "8px 0" }}>
          {validationTitle}
        </Text>
        <div style={{ margin: "0 0 16px 24px" }}>
          {validationData.map((itemData, index) => (
            <Flex
              key={index}
              $justifyContent="flex-start"
              style={{ marginBottom: 8 }}
            >
              {checkPasswordCondition(itemData)}
              <Text style={{ marginLeft: 8 }}>{itemData.text}</Text>
            </Flex>
          ))}
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <Form.Item
        name="password"
        label={passwordLabel || t("password_section.password_label")}
        style={{ margin: "0 0 16px" }}
        rules={[
          {
            validator: (_, value) => {
              const isValid = onPasswordChange(value);
              if (isValid) return Promise.resolve();
            },
          },
        ]}
      >
        <Input.Password
          placeholder={
            showPlaceholder
              ? t("password_section.password_placeholder")
              : undefined
          }
          size={size}
          onBlur={onBlur}
          onFocus={onFocus}
          data-testid={passwordInputTestId}
          autoComplete={autoComplete}
        />
      </Form.Item>
      {showPasswordValidation && getPasswordErrorMessage()}

      {showConfirm && (
        <Form.Item
          name="confirmPassword"
          label={
            confirmLabel ||
            t("password_section.confirm_label", {
              label: passwordLabel || t("password_section.password_label"),
            })
          }
          dependencies={["password"]}
          hasFeedback
          rules={[
            {
              required: true,
              message: t("password_section.confirm_required"),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  t("password_section.passwords_not_match")
                );
              },
            }),
          ]}
        >
          <Input.Password
            placeholder={
              showPlaceholder
                ? t("password_section.confirm_placeholder")
                : undefined
            }
            size={size}
            autoComplete={autoComplete}
          />
        </Form.Item>
      )}
    </>
  );
};

const Rules = styled.div`
  display: grid;
  gap: 6px;
  margin: -4px 0 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.cardLightBgColor};
  border: 1px solid ${(p) => p.theme.colors.cardBorderColor};
`;
const RulesTitle = styled.div`
  margin-bottom: 2px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12.5px;
`;
const Rule = styled.div<{ $valid: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${(p) =>
    p.$valid ? p.theme.colors.primaryText : p.theme.colors.secondaryText};
  font-size: 13px;
  line-height: 1.4;
  svg {
    flex: none;
  }
`;
